import React, { useEffect, useState } from 'react';

const initialState = {
  nombre: '',
  codigo: '',
  codigo_barras: '',
  categoria_id: '',
  precio: '',
  costo: '',
  stock_actual: '',
  stock_minimo: '',
};

const inputClass =
  'h-11 w-full rounded-xl border border-blanco-puro/20 bg-azul-oscuro/70 px-3 text-blanco-puro placeholder:text-blanco-puro/40 outline-none transition focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/40';

export default function ProductoFormModal({
  abierto,
  producto,
  categorias = [],
  guardando,
  error,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(initialState);

  const editando = Boolean(producto?.id);

  useEffect(() => {
    if (!abierto) return;

    if (editando) {
      setForm({
        nombre: producto.nombre || '',
        codigo: producto.codigo || '',
        codigo_barras: producto.codigo_barras || '',
        categoria_id: producto.categoria_id ?? '',
        precio: producto.precio ?? '',
        costo: producto.costo ?? '',
        stock_actual: producto.stock_actual ?? '',
        stock_minimo: producto.stock_minimo ?? '',
      });
    } else {
      setForm(initialState);
    }
  }, [abierto, editando, producto]);

  if (!abierto) return null;

  const setField = (field, value) => {
    setForm((actual) => ({
      ...actual,
      [field]: value,
    }));
  };

  const submit = (event) => {
    event.preventDefault();

    onSubmit({
      nombre: form.nombre,
      codigo: form.codigo || null,
      codigo_barras: form.codigo_barras || null,
      categoria_id:
        form.categoria_id === ''
          ? null
          : Number(form.categoria_id),
      precio: form.precio,
      costo: form.costo === '' ? null : form.costo,
      stock_minimo: form.stock_minimo || 0,
      ...(editando ? {} : { stock_actual: form.stock_actual || 0 }),
    });
  };

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center bg-black/70 p-4 backdrop-blur-sm">
      <form
        onSubmit={submit}
        className="w-full max-w-xl overflow-hidden rounded-2xl border border-blanco-puro/20 bg-gradient-to-br from-azul-oscuro to-azul-lago text-blanco-puro shadow-2xl"
      >
        <div className="border-b border-blanco-puro/15 px-6 py-5">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-amarillo-dorado">
            Driza Camp · Cantina
          </div>

          <h2 className="mt-1 text-2xl font-bold text-blanco-puro">
            {editando ? 'Editar producto' : 'Nuevo producto'}
          </h2>
        </div>

        <div className="grid gap-4 p-6 sm:grid-cols-2">
          <label className="sm:col-span-2">
            <span className="mb-1 block text-xs font-bold text-amarillo-dorado">
              Nombre
            </span>

            <input
              autoFocus
              value={form.nombre}
              onChange={(e) => setField('nombre', e.target.value)}
              className={inputClass}
              required
            />
          </label>

          <label className="sm:col-span-2">
            <span className="mb-1 block text-xs font-bold text-blanco-puro/70">
              Categoría
            </span>

            <select
              value={form.categoria_id}
              onChange={(e) =>
                setField('categoria_id', e.target.value)
              }
              className={inputClass}
            >
              <option value="" className="text-azul-oscuro">
                Sin categoría
              </option>

              {categorias.map((categoria) => (
                <option
                  key={categoria.id}
                  value={categoria.id}
                  className="text-azul-oscuro"
                >
                  {categoria.nombre}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span className="mb-1 block text-xs font-bold text-blanco-puro/70">
              Código
            </span>

            <input
              value={form.codigo}
              onChange={(e) => setField('codigo', e.target.value)}
              className={inputClass}
            />
          </label>

          <label>
            <span className="mb-1 block text-xs font-bold text-blanco-puro/70">
              Código de barras
            </span>

            <input
              value={form.codigo_barras}
              onChange={(e) => setField('codigo_barras', e.target.value)}
              className={inputClass}
            />
          </label>

          <label>
            <span className="mb-1 block text-xs font-bold text-blanco-puro/70">
              Precio
            </span>

            <input
              type="number"
              min="0"
              step="0.01"
              value={form.precio}
              onChange={(e) => setField('precio', e.target.value)}
              className={inputClass}
              required
            />
          </label>

          <label>
            <span className="mb-1 block text-xs font-bold text-blanco-puro/70">
              Costo
            </span>

            <input
              type="number"
              min="0"
              step="0.01"
              value={form.costo}
              onChange={(e) => setField('costo', e.target.value)}
              className={inputClass}
            />
          </label>

          {!editando && (
            <label>
              <span className="mb-1 block text-xs font-bold text-blanco-puro/70">
                Stock inicial
              </span>

              <input
                type="number"
                min="0"
                step="0.001"
                value={form.stock_actual}
                onChange={(e) => setField('stock_actual', e.target.value)}
                className={inputClass}
              />
            </label>
          )}

          <label>
            <span className="mb-1 block text-xs font-bold text-blanco-puro/70">
              Stock mínimo
            </span>

            <input
              type="number"
              min="0"
              step="0.001"
              value={form.stock_minimo}
              onChange={(e) => setField('stock_minimo', e.target.value)}
              className={inputClass}
            />
          </label>

          {error && (
            <div className="sm:col-span-2 rounded-xl border border-red-300/30 bg-red-500/20 px-3 py-2 text-sm text-red-100">
              {error}
            </div>
          )}

          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={guardando}
              className="rounded-xl border border-blanco-puro/30 bg-blanco-puro/5 px-4 py-2 text-sm font-semibold text-blanco-puro transition hover:bg-blanco-puro/10 disabled:opacity-50"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={guardando}
              className="rounded-xl bg-amarillo-dorado px-5 py-2 text-sm font-bold text-azul-oscuro shadow-lg transition hover:bg-amber-400 disabled:opacity-50"
            >
              {guardando
                ? 'Guardando...'
                : editando
                  ? 'Guardar cambios'
                  : 'Crear producto'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
